import { Icon } from '@iconify/react';
import { useMutation, useQuery } from '@tanstack/react-query';
/**
 * "I'm here" and "I'm done".
 *
 * The timesheet has always been something the owner typed in afterwards, from
 * memory — the least reliable source in the house and the one both sides end up
 * arguing with. One tap when she arrives is the only record either of them can
 * point at later.
 */
import { api } from '../api';
import { toastError, toastOk } from '../components/Toasts';
import { t } from '../i18n';
import { get, invalidate } from '../query';

interface Shift {
  id: string;
  started_at: string;
  ended_at: string | null;
  minutes: number | null;
}

function elapsed(startedAt: string): string {
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(startedAt)) / 60_000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function ClockCard() {
  const q = useQuery({
    queryKey: ['/v1/shifts/current'],
    queryFn: get<{ shift: Shift | null }>,
    // A running shift's elapsed time is the whole point of the card.
    refetchInterval: 60_000,
  });
  const shift = q.data?.shift ?? null;

  const clock = useMutation({
    mutationFn: (dir: 'in' | 'out') =>
      api(`/v1/shifts/clock-${dir}`, { method: 'POST', body: '{}' }),
    onSuccess: (_r, dir) => toastOk(dir === 'in' ? t('Clocked in') : t('Clocked out')),
    onError: (e) => toastError(e),
    onSettled: () => invalidate('/v1/shifts', '/v1/payroll/work-entries'),
  });

  if (q.isPending) return null;

  return (
    <button
      type="button"
      disabled={clock.isPending}
      onClick={() => clock.mutate(shift ? 'out' : 'in')}
      className={`flex w-full items-center justify-between gap-4 rounded-[2rem] p-5 text-start transition-transform active:scale-[0.99] disabled:opacity-50 ${
        shift ? 'bg-task text-white' : 'border border-border bg-card'
      }`}
    >
      <span className="flex min-w-0 items-center gap-4">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
            shift ? 'bg-white/20' : 'bg-secondary text-muted-foreground'
          }`}
        >
          <Icon icon={shift ? 'ph:stop-circle-bold' : 'ph:play-circle-bold'} className="text-2xl" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-bold">
            {shift ? t("I'm done") : t("I'm here")}
          </span>
          <span
            className={`mt-0.5 block truncate text-[10px] font-bold uppercase tracking-widest ${
              shift ? 'text-white/70' : 'text-muted-foreground'
            }`}
          >
            {shift
              ? t('Working for {{time}}').replace('{{time}}', elapsed(shift.started_at))
              : t('Tap when you arrive')}
          </span>
        </span>
      </span>
    </button>
  );
}
